/**
 * LandingPage.jsx — full cinematic landing (logged-out gate).
 *
 * Structure: HeroPrism hero → counters → INDICATORS section (master-indicators
 * headline + 8 expandable scene cards) → TERMINAL section (master-pages +
 * 4 page cards) → pricing CTA → footer/legal. All live scenes come from
 * src/landing/scenes-*.js (window.Scenes registry, SceneEngine chrome) and
 * mount lazily via IntersectionObserver / card expansion. Fully bilingual
 * (RU/EN) via useI18n + the local LT dictionary; the nav has the switcher —
 * scenes remount on language change (key includes lang).
 */
import { useEffect, useRef, useState } from 'react';
import AuthModal from './AuthModal';
import { createHeroPrism } from '../landing/HeroPrism';
import { useI18n, setLang } from '../hooks/useI18n';
import { AUTH_API_URL } from '../config';
import '../landing.css';
import './LandingPage.css';
import '../landing/landing-design.css';
import '../landing/landing-cinematic.css';
import { tr } from '@quantum/ui';
// Scenes are code-split, NOT imported eagerly. scenes-pages pulls in ThreeMap →
// the whole Three.js stack; as a static import that landed in the landing page's
// critical bundle, so the public entry point had to download and parse the entire
// 3D engine before it could respond to anything — which is what made the first
// "watch the scene" click feel slow.
//
// The chunk is warmed right after first paint and again on card hover, so by the
// time anyone reaches a card it's already parsed and the click is instant.
let _scenesPromise = null;
export function preloadScenes() {
  if (!_scenesPromise) {
    _scenesPromise = Promise.all([
      import('../landing/scene-engine'),
      import('../landing/scenes-indicators'),
      import('../landing/scenes-pages'),
      import('../landing/scenes-master'),
    ]);
  }
  return _scenesPromise;
}

// ── Brand atom logo (matches Navigation.jsx AtomLogo) ───────────────────────
function QuantumAtom({ size = 22 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" fill="none">
      <g stroke="#79d4bb" strokeWidth="3.4" fill="none" strokeLinecap="round">
        <circle cx="32" cy="30" r="10.5" />
        <ellipse cx="32" cy="30" rx="10.5" ry="23" transform="rotate(34 32 30)" />
        <ellipse cx="32" cy="30" rx="10.5" ry="23" transform="rotate(-34 32 30)" />
      </g>
      <g fill="#79d4bb">
        <circle cx="22.5" cy="13.5" r="3.2" />
        <circle cx="41.5" cy="13.5" r="3.2" />
        <circle cx="15" cy="44" r="3.4" />
        <circle cx="49" cy="44" r="3.4" />
      </g>
    </svg>
  );
}

// ── landing-local dictionary (nav/hero/sections/cards) ──────────────────────
const LT = {
  ru: {
    navInd: 'Индикаторы', navTerm: 'Терминал', navPrice: 'Тарифы',
    signIn: 'Войти',
    heroBadge1: 'Проект в стадии', heroBadge2: '— полный доступ', heroBadge3: 'БЕСПЛАТЕН',
    heroTitle1: 'Видь рынок насквозь.', heroTitle2: 'Торгуй с преимуществом.',
    heroSub: 'Скринер 300+ фьючерсных пар Binance, 3D-карта ликвидности, 8 боевых индикаторов и AI-оценка каждой сделки. Виртуальные сделки системы и людей — в реальном времени.',
    heroCta: 'Открыть терминал', heroGhost: 'Смотреть индикаторы',
    c1: 'СДЕЛОК ОПУБЛИКОВАНО', c2: 'ВИН-РЕЙТ ОТРАБОТАННЫХ', c3: 'ЗАДЕРЖКА ДАННЫХ', c4: 'АКТИВНЫХ ПОЛЬЗОВАТЕЛЕЙ',
    indEyebrow: 'INDICATORS', indH2: 'Индикаторы, которые видят сделку заранее',
    indP: 'Каждый ролик — реальная механика индикатора, кадр в кадр как в терминале. 30 секунд — одна отработанная сделка.',
    masterIndTitle: 'quantum-terminal / confluence — 8 индикаторов, один сетап',
    pgEyebrow: 'THE TERMINAL', pgH2: 'Один трейд — весь терминал',
    pgP: 'Радар находит, скринер исполняет, сообщество усиливает, вотчлист сопровождает. Всё — не покидая вкладку.',
    watch: 'СМОТРЕТЬ СЦЕНУ', live: 'LIVE SIM',
    priceH2: 'Готов торговать с преимуществом?',
    priceP: 'Полный доступ ко всем инструментам бесплатно на время беты. Без карты, без лимитов.',
    priceBadge: 'BETA ACCESS · $0 на время беты',
    devEyebrow: 'В РАЗРАБОТКЕ',
    devH: 'Что делаем дальше',
    devP: 'Помимо сайта сейчас собираем торговый скальпинг-терминал и приложение, которое объединит его со скринером в одном окне. Обе вещи ещё сырые и показывать их рано.',
    devTest: 'Нужны тестировщики. Если готов поломать раннюю сборку и рассказать, где неудобно — напиши, откроем доступ.',
    devReward: 'За помощь в разработке даём подписки, когда бета закончится.',
    devCta: 'Подать заявку на тест',
    legalBtn: 'Дисклеймер о рисках',
    legal1: 'Quantum — аналитический инструмент, а не инвестиционный советник. Торговля фьючерсами на криптовалюты сопряжена с высоким риском: вы можете потерять весь депозит.',
    legal2: 'Сцены на этой странице — симуляции на исторических и синтетических данных для демонстрации механики индикаторов; они не являются гарантией доходности. Показатели win-rate и P/L в роликах иллюстративны.',
    legal3: 'API-ключи Binance хранятся только в вашем браузере и не передаются на наши серверы.',
    footer: '© 2026 Quantum · Beta · All systems nominal',
  },
  en: {
    navInd: 'Indicators', navTerm: 'Terminal', navPrice: 'Pricing',
    signIn: 'Sign in',
    heroBadge1: 'Project is in', heroBadge2: '— full access is', heroBadge3: 'FREE',
    heroTitle1: 'See through the market.', heroTitle2: 'Trade with an edge.',
    heroSub: 'A screener for 300+ Binance futures pairs, a 3D liquidity map, 8 battle-tested indicators and an AI score on every trade. Virtual trades from the system and its users — in real time.',
    heroCta: 'Launch terminal', heroGhost: 'Explore indicators',
    c1: 'TRADES POSTED', c2: 'SETTLED WIN-RATE', c3: 'DATA LATENCY', c4: 'ACTIVE USERS',
    indEyebrow: 'INDICATORS', indH2: 'Indicators that see the trade in advance',
    indP: 'Every clip is the indicator’s real mechanics — frame for frame as in the terminal. 30 seconds, one completed trade.',
    masterIndTitle: 'quantum-terminal / confluence — 8 indicators, one setup',
    pgEyebrow: 'THE TERMINAL', pgH2: 'One trade — the whole terminal',
    pgP: 'The radar finds it, the screener executes it, the community amplifies it, the watchlist tracks it. All without leaving the tab.',
    watch: 'WATCH THE SCENE', live: 'LIVE SIM',
    priceH2: 'Ready to trade with an edge?',
    priceP: 'Full access to every tool, free during the beta. No card, no limits.',
    priceBadge: 'BETA ACCESS · $0 during beta',
    devEyebrow: 'IN DEVELOPMENT',
    devH: 'What we are building next',
    devP: 'Alongside the site we are building a scalping trade terminal and an app that puts it and the screener in one window. Both are still rough and not ready to show.',
    devTest: 'We need testers. If you are willing to break an early build and tell us where it hurts, write to us and we will open access.',
    devReward: 'Everyone who helps during development gets a subscription once the beta ends.',
    devCta: 'Apply to test',
    legalBtn: 'Risk disclaimer',
    legal1: 'Quantum is an analytics tool, not an investment advisor. Trading crypto futures carries high risk: you can lose your entire deposit.',
    legal2: 'The scenes on this page are simulations over historical and synthetic data that demonstrate indicator mechanics; they are not a promise of returns. Win-rates and P/L shown in the clips are illustrative.',
    legal3: 'Binance API keys are stored only in your browser and are never sent to our servers.',
    footer: '© 2026 Quantum · Beta · All systems nominal',
  },
};

const IND_TABS = [
  { id: 'ind-heat', tag: 'HEAT', c: '#7bcdf5', t: { ru: 'Liq Heatmap', en: 'Liq Heatmap' }, d: { ru: 'Карта скоплений ликвидаций по четырём плечам: видно, куда цену потянет за топливом.', en: 'A four-tier liquidation density map: see where price will be pulled for fuel.' } },
  { id: 'ind-walls', tag: 'WALLS', c: '#e89aa3', t: { ru: 'Smart Walls', en: 'Smart Walls' }, d: { ru: 'Живые стены заявок с возрастом и z-score — спуфинг отсеивается сам.', en: 'Live order walls with age and z-score — spoofing filters itself out.' } },
  { id: 'ind-pvt', tag: 'PVT', c: '#8aabe6', t: { ru: 'Pivot S/R', en: 'Pivot S/R' }, d: { ru: 'Скопления фитилей — карманы ликвидности.', en: 'Wick clusters are liquidity pockets.' } },
  { id: 'ind-lvl', tag: 'LVL', c: '#f5c846', t: { ru: 'Global S/R', en: 'Global S/R' }, d: { ru: 'Уровни D1–M15 с касаниями и свежестью.', en: 'D1–M15 levels with touch counts and freshness.' } },
  { id: 'ind-acc', tag: 'ACC', c: '#79d4bb', t: { ru: 'Accumulation', en: 'Accumulation' }, d: { ru: 'Боксы тихого набора: пробой перекрашивает зону.', en: 'Quiet-accumulation boxes: breakout recolors the zone.' } },
  { id: 'ind-frvp', tag: 'FRVP', c: '#00f2ff', t: { ru: 'Volume Profile', en: 'Volume Profile' }, d: { ru: 'POC-магнит и вакуумы низкого объёма.', en: 'The POC magnet and low-volume vacuums.' } },
  { id: 'ind-onchain', tag: 'CHAIN', c: '#8aabe6', t: { ru: 'On-chain X-Ray', en: 'On-chain X-Ray' }, d: { ru: 'Киты, потоки бирж — движение видно раньше графика.', en: 'Whales, exchange flows — the move shows before the chart.' } },
  { id: 'ind-grav', tag: 'GRAV', c: '#bd9ce0', t: { ru: 'Institutional Gravity', en: 'Institutional Gravity' }, d: { ru: 'Магниты PDH/PDL, FVG-вакуумы, ловушки ликвидности и цели Вайкоффа.', en: 'PDH/PDL magnets, FVG vacuums, liquidity traps and Wyckoff targets.' } },
  { id: '_coming-ind', tag: '...', c: 'rgba(160,180,210,0.35)', coming: true, t: { ru: 'Coming Soon', en: 'Coming Soon' }, d: { ru: 'Новые индикаторы уже в разработке.', en: 'More indicators are in development.' } },
];
const PAGE_TABS = [
  { id: 'page-radar', tag: 'RADAR', c: '#e89aa3', t: { ru: 'Density Radar', en: 'Density Radar' }, d: { ru: '3D-карта ликвидности 698 рынков.', en: 'A 3D liquidity map of 698 markets.' } },
  { id: 'page-screener', tag: 'QUANT', c: '#79d4bb', t: { ru: 'Quant Screener', en: 'Quant Screener' }, d: { ru: 'До 4 графиков, все индикаторы.', en: 'Up to 4 charts, every indicator.' } },
  { id: 'page-situations', tag: 'INTEL', c: '#8aabe6', t: { ru: 'Situations', en: 'Situations' }, d: { ru: 'AI-оценённые сетапы — копируй в клик.', en: 'AI-scored setups — copy in one click.' } },
  { id: 'page-watchlist', tag: 'GRID', c: '#8b7bf0', t: { ru: 'Grid Watchlist', en: 'Grid Watchlist' }, d: { ru: '9 живых графиков в сетке 3×3.', en: '9 live charts in a 3×3 grid.' } },
  { id: 'page-onchain', tag: 'CHAIN', c: '#34d3e0', t: { ru: 'On-chain', en: 'On-chain' }, d: { ru: 'Киты, потоки бирж и разлоки.', en: 'Whales, exchange flows and unlocks.' } },
  { id: '_coming-page', tag: '...', c: 'rgba(160,180,210,0.35)', coming: true, t: { ru: 'Coming Soon', en: 'Coming Soon' }, d: { ru: 'Новые страницы терминала уже в разработке.', en: 'More terminal pages are in development.' } },
];

// Counters come from the PUBLIC stats route — no placeholder numbers on the
// landing. Until the request lands (or if it fails) each cell shows "—" rather
// than a made-up figure.
function fmtCount(n) {
  if (n == null) return '—';
  if (n >= 1000) return n.toLocaleString('en-US').replace(/,/g, ' ');
  return String(n);
}

function useLandingStats() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let alive = true;
    fetch(`${AUTH_API_URL}/stats/public`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (alive && d) setStats(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);
  return stats;
}

// ── scene plumbing ───────────────────────────────────────────────────────────
function SceneMount({ id }) {
  const ref = useRef(null);
  useEffect(() => {
    let inst = null, alive = true;
    // Await the chunk: clicking before it lands used to find window.Scenes empty
    // and silently render nothing. Normally it's already warm and this resolves
    // on the spot.
    preloadScenes().then(() => {
      if (!alive) return;
      const el = ref.current;
      const scene = window.Scenes?.[id];
      if (!el || !scene) return;
      try { inst = scene.mount(el); } catch (e) { console.error('scene mount failed:', id, e); }
    });
    return () => {
      alive = false;
      if (inst) { try { inst.destroy(); } catch { /* torn down */ } }
    };
  }, [id]);
  return <div className="scene-body" ref={ref} />;
}

function useInView(margin = '160px') {
  const ref = useRef(null);
  const [inView, set] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(([e]) => set(e.isIntersecting), { rootMargin: margin });
    io.observe(el);
    return () => io.disconnect();
  }, [margin]);
  return [ref, inView];
}

function SceneBar({ path, live }) {
  return (
    <div className="scene-bar">
      <span className="dot" style={{ background: '#e89aa3' }} />
      <span className="dot" style={{ background: '#e6c188' }} />
      <span className="dot" style={{ background: '#79d4bb' }} />
      <span className="scene-path">{path}</span>
      <span className="scene-live"><span className="ld" />{live}</span>
    </div>
  );
}

// headline master scene — plays automatically while visible
function MasterScene({ id, path, live, lang }) {
  const [ref, inView] = useInView('120px');
  return (
    <div className="scard open reveal" ref={ref} style={{ '--c': '#34d3e0', '--c-bd': 'rgba(52,211,224,0.30)' }}>
      <div className="scene-wrap">
        <SceneBar path={path} live={live} />
        {inView ? <SceneMount id={id} key={id + lang} /> : <div className="scene-body" />}
      </div>
    </div>
  );
}

// Tab bar + single viewport for indicator scenes
function TabSwitcher({ tabs, active, onSelect, lang }) {
  return (
    <div className="lp-tabs" onPointerEnter={preloadScenes}>
      {tabs.map((d) => (
        <button
          key={d.id}
          className={'lp-tab' + (active === d.id ? ' on' : '') + (d.coming ? ' coming' : '')}
          style={{ '--c': d.c }}
          onClick={() => !d.coming && onSelect(d.id)}
          disabled={d.coming}
        >
          <span className="lp-tab-tag mono">{d.tag}</span>
          <span className="lp-tab-name">{d.t[lang]}</span>
        </button>
      ))}
    </div>
  );
}

function TabViewport({ tabs, active, lang, live }) {
  const d = tabs.find((t) => t.id === active) || tabs[0];
  return (
    <div className="lp-viewport" style={{ '--c': d.c, '--c-bd': d.c + '4d' }}>
      <div className="lp-viewport-desc">{d.d[lang]}</div>
      <div className="scene-wrap" style={{ display: 'block' }}>
        <SceneBar path={`quantum-terminal / ${d.id}`} live={live} />
        <SceneMount id={d.id} key={d.id + lang} />
      </div>
    </div>
  );
}

// ── Main ─────────────────────────────────────────────────────────────────────
export default function LandingPage() {
  const { lang } = useI18n();
  const T = LT[lang] || LT.en;
  const [authOpen, setAuthOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeInd, setActiveInd] = useState('ind-heat');
  const [activePage, setActivePage] = useState('page-radar');
  const [legalOpen, setLegalOpen] = useState(false);
  const stats = useLandingStats();
  const prismMountRef = useRef(null);
  const rootRef = useRef(null);

  // Warm the scene chunk once the page is painted and the browser is idle, so the
  // first "watch the scene" click has nothing left to download. Off the critical
  // path by construction — requestIdleCallback yields to anything more urgent.
  useEffect(() => {
    const ric = window.requestIdleCallback || ((fn) => setTimeout(fn, 800));
    const id = ric(() => { preloadScenes(); });
    return () => { (window.cancelIdleCallback || clearTimeout)(id); };
  }, []);

  // unlock document scrolling while the landing is mounted (dashboard locks it)
  useEffect(() => {
    const root = document.getElementById('root');
    const prev = {
      ho: document.documentElement.style.overflow, hh: document.documentElement.style.height,
      bo: document.body.style.overflow, bh: document.body.style.height,
      ro: root ? root.style.overflow : '', rh: root ? root.style.height : '',
      rd: root ? root.style.display : '', rw: root ? root.style.width : '',
    };
    document.documentElement.style.overflow = 'auto'; document.documentElement.style.height = 'auto';
    document.body.style.overflow = 'auto'; document.body.style.height = 'auto';
    if (root) { root.style.overflow = 'auto'; root.style.height = 'auto'; root.style.display = 'block'; root.style.width = '100%'; }
    return () => {
      document.documentElement.style.overflow = prev.ho; document.documentElement.style.height = prev.hh;
      document.body.style.overflow = prev.bo; document.body.style.height = prev.bh;
      if (root) { root.style.overflow = prev.ro; root.style.height = prev.rh; root.style.display = prev.rd; root.style.width = prev.rw; }
    };
  }, []);

  // 3D hero prism inside the hero stage
  useEffect(() => {
    const mount = prismMountRef.current;
    if (!mount) return undefined;
    let ctl = null;
    try { ctl = createHeroPrism(mount); } catch (e) { console.error('hero prism failed', e); }
    return () => { if (ctl) { try { ctl.dispose(); } catch { /* torn down */ } } };
  }, []);

  // sticky-nav shading + scroll reveal
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => {
    const els = rootRef.current?.querySelectorAll('.reveal');
    if (!els?.length) return undefined;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('in'); }),
      { rootMargin: '-40px' },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [lang]);

  const goto = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="lp" ref={rootRef}>
      <div className="bg-wrap">
        <div className="glow a" /><div className="glow b" /><div className="glow c" />
        <div className="grid-overlay" />
      </div>

      {/* ── nav ── */}
      <nav className={scrolled ? 'scrolled' : ''}>
        <div className="logo">
          <div className="logo-mark"><QuantumAtom size={22} /></div>
          <span className="logo-text">Quantum</span>
          <span className="beta-tag">{tr('BETA')}</span>
        </div>
        <div className="nav-links">
          <a onClick={() => goto('indicators')} style={{ cursor: 'pointer' }}>{T.navInd}</a>
          <a onClick={() => goto('terminal')} style={{ cursor: 'pointer' }}>{T.navTerm}</a>
          <a onClick={() => goto('pricing')} style={{ cursor: 'pointer' }}>{T.navPrice}</a>
        </div>
        <div className="lang-sw mono">
          <button className={lang === 'ru' ? 'on' : ''} onClick={() => setLang('ru')}>RU</button>
          <button className={lang === 'en' ? 'on' : ''} onClick={() => setLang('en')}>EN</button>
        </div>
        <button className="nav-cta" onClick={() => setAuthOpen(true)}>{T.signIn}</button>
      </nav>

      {/* ── hero (HeroPrism stays) ── */}
      <header className="hero-stage">
        <div className="hero-prism" ref={prismMountRef} />
        <div className="hero-scrim" />
        <section className="hero container">
          <span className="beta-badge">
            <span className="beta-dot" />
            {T.heroBadge1} <b>{tr('Beta')}</b> {T.heroBadge2} <b>{T.heroBadge3}</b>
          </span>
          <h1>{T.heroTitle1}<br /><span className="grad-txt">{T.heroTitle2}</span></h1>
          <p>{T.heroSub}</p>
          <div className="hero-cta">
            <button className="btn-primary" onClick={() => setAuthOpen(true)}>
              {T.heroCta}
              <svg width="15" height="15" viewBox="0 0 14 14" fill="none"><path d="M3 7 H10 M7 4 L10 7 L7 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
            <button className="btn-ghost" onClick={() => goto('indicators')}>{T.heroGhost}</button>
          </div>
          <div className="counters hero-counters">
            <div className="counter"><div className="num" style={{ color: '#79d4bb' }}>{fmtCount(stats?.tradesPosted)}</div><div className="lbl">{T.c1}</div></div>
            <div className="counter"><div className="num" style={{ color: '#34d3e0' }}>{stats?.winRate != null ? `${stats.winRate}%` : '—'}</div><div className="lbl">{T.c2}</div></div>
            <div className="counter"><div className="num" style={{ color: '#e6c188' }}>14ms</div><div className="lbl">{T.c3}</div></div>
            <div className="counter"><div className="num" style={{ color: '#fff' }}>{fmtCount(stats?.activeUsers)}</div><div className="lbl">{T.c4}</div></div>
          </div>
        </section>
      </header>

      <main className="container">
        {/* ── indicators ── */}
        <section className="section" id="indicators">
          <div className="section-head reveal">
            <div className="section-eyebrow">{T.indEyebrow}</div>
            <h2>{T.indH2}</h2>
            <p>{T.indP}</p>
          </div>
          <MasterScene id="master-indicators" path={T.masterIndTitle} live={T.live} lang={lang} />
          <TabSwitcher tabs={IND_TABS} active={activeInd} onSelect={setActiveInd} lang={lang} />
          <TabViewport tabs={IND_TABS} active={activeInd} lang={lang} live={T.live} />
        </section>

        {/* ── terminal pages ── */}
        <section className="section" id="terminal" style={{ paddingTop: 0 }}>
          <div className="section-head reveal">
            <div className="section-eyebrow">{T.pgEyebrow}</div>
            <h2>{T.pgH2}</h2>
            <p>{T.pgP}</p>
          </div>
          <TabSwitcher tabs={PAGE_TABS} active={activePage} onSelect={setActivePage} lang={lang} />
          <TabViewport tabs={PAGE_TABS} active={activePage} lang={lang} live={T.live} />
        </section>

        {/* ── pricing / CTA ── */}
        <section className="cta-band reveal" id="pricing">
          <span className="beta-badge" style={{ marginBottom: 24 }}>
            <span className="beta-dot" />
            {T.priceBadge}
          </span>
          <h2>{T.priceH2}</h2>
          <p>{T.priceP}</p>
          <div className="hero-cta" style={{ marginTop: 30 }}>
            <button className="btn-primary" onClick={() => setAuthOpen(true)}>
              {T.heroCta}
              <svg width="15" height="15" viewBox="0 0 14 14" fill="none"><path d="M3 7 H10 M7 4 L10 7 L7 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </button>
          </div>
        </section>

        {/* ── in-development note ──
            Deliberately quiet: the owner asked for this to read as a footnote, not
            a second sales pitch. No accent band, no big type, sits right above the
            footer. */}
        <section className="dev-note reveal">
          <span className="dev-eyebrow mono">{T.devEyebrow}</span>
          <h3 className="dev-h">{T.devH}</h3>
          <p className="dev-p">{T.devP}</p>
          <p className="dev-p">{T.devTest}</p>
          <p className="dev-p dim">{T.devReward}</p>
          <a
            className="dev-cta mono"
            href="mailto:beta@quan.trading?subject=Quantum%20beta%20test%20application"
          >{T.devCta} →</a>
        </section>

        {/* ── footer ── */}
        <footer>
          <div className="foot-top">
            <div className="logo">
              <div className="logo-mark" style={{ width: 28, height: 28, borderRadius: 8 }}><QuantumAtom size={16} /></div>
              <span style={{ fontWeight: 500, color: 'var(--txt2)' }}>Quantum</span>
            </div>
            <span className="mono" style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--txt3)' }}>{T.footer}</span>
          </div>
          <div className={'legal' + (legalOpen ? ' open' : '')}>
            <button className="legal-btn" onClick={() => setLegalOpen((v) => !v)}>
              <span className="h">{T.legalBtn}</span>
              <span className="ar">▸</span>
            </button>
            <div className="legal-body">
              <div className="legal-inner">
                <p><strong>{T.legal1}</strong></p>
                <p>{T.legal2}</p>
                <p>{T.legal3}</p>
              </div>
            </div>
          </div>
        </footer>
      </main>

      {/* auth modal */}
      {authOpen && (
        <div className="fixed inset-0 z-[200]">
          <AuthModal onClose={() => setAuthOpen(false)} />
        </div>
      )}
    </div>
  );
}
